import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CreditCardIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ArrowLeftIcon,
  CurrencyDollarIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../context/AuthContext';
import { publicAPI, bookingAPI } from '../services/api';
import toast from 'react-hot-toast';

const BookService = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [services, setServices] = useState([]);
  const [clientServices, setClientServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedService, setSelectedService] = useState(null);
  const [showPayment, setShowPayment] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [isAuthenticated, navigate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [servicesData, clientServicesData] = await Promise.all([
        publicAPI.getServices(),
        bookingAPI.getMyClientServices()
      ]);
      
      setServices(Array.isArray(servicesData) ? servicesData : (servicesData.results || []));
      setClientServices(Array.isArray(clientServicesData) ? clientServicesData : (clientServicesData.results || []));
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement des services');
    } finally {
      setLoading(false);
    }
  };

  const hasActiveService = () => {
    return clientServices.some(service => service.status === 'active');
  };

  const handleSelectService = (service) => {
    // Allow multiple active services - no restriction
    setSelectedService(service);
    setShowPayment(true);
  };

  const handlePaymentSuccess = () => {
    navigate('/book-service/confirmation', { 
      state: { 
        service: selectedService,
        user: user 
      } 
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (showPayment) {
    return (
      <PaymentPage 
        service={selectedService}
        onBack={() => setShowPayment(false)}
        onSuccess={handlePaymentSuccess}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Retour à l'accueil
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Choisir un service</h1>
          <p className="text-gray-600 mt-2">
            Sélectionnez le pack ou l'abonnement qui vous convient
          </p>
        </div>

        {/* Multiple Services Info */}
        {hasActiveService() && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <div className="flex items-start space-x-3">
              <CheckCircleIcon className="h-5 w-5 text-green-600 mt-0.5" />
              <div>
                <h3 className="text-green-800 font-medium">Services multiples autorisés</h3>
                <p className="text-green-700 text-sm mt-1">
                  Vous pouvez avoir plusieurs packs et abonnements actifs en même temps.
                  Consultez votre profil pour voir tous vos services.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service) => (
            <ServiceCard
              key={service.id}
              service={service}
              onSelect={handleSelectService}
              disabled={false}
            />
          ))}
        </div>

        {services.length === 0 && (
          <div className="text-center py-12">
            <CreditCardIcon className="h-12 w-12 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun service disponible</h3>
            <p className="text-gray-600">Les services seront bientôt disponibles.</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Service Card Component
const ServiceCard = ({ service, onSelect, disabled }) => {
  return (
    <div className={`bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden transition-all duration-200 ${
      disabled ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-md hover:border-primary-300'
    }`}>
      <div className="p-6">
        {/* Service Type Badge */}
        <div className="flex items-center justify-between mb-4">
          <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
            service.type === 'pack' 
              ? 'bg-blue-100 text-blue-800' 
              : 'bg-purple-100 text-purple-800'
          }`}>
            {service.type === 'pack' ? 'Pack' : 'Abonnement'}
          </span>
          {service.is_active && (
            <CheckCircleIcon className="h-5 w-5 text-green-500" />
          )}
        </div>

        {/* Service Name */}
        <h3 className="text-xl font-bold text-gray-900 mb-2">{service.name}</h3>
        
        {/* Description */}
        <p className="text-gray-600 text-sm mb-4">{service.description}</p>

        {/* Service Details */}
        <div className="space-y-3 mb-6">
          <div className="flex items-center justify-between">
            <span className="text-gray-500 text-sm">Séances:</span>
            <span className="font-medium">
              {service.session_count} {service.type === 'abonnement' && '/ mois'}
            </span>
          </div>
          
          {service.duration_days && (
            <div className="flex items-center justify-between">
              <span className="text-gray-500 text-sm">Validité:</span>
              <span className="font-medium">{service.duration_days} jours</span>
            </div>
          )}
          
          <div className="flex items-center justify-between">
            <span className="text-gray-500 text-sm">Prix:</span>
            <span className="text-2xl font-bold text-primary-600">
              {service.price} MAD
            </span>
          </div>
        </div>

        {/* Select Button */}
        <button
          onClick={() => onSelect(service)}
          disabled={!service.is_active}
          className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
            !service.is_active
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-primary-600 hover:bg-primary-700 text-white'
          }`}
        >
          {!service.is_active ? 'Service non disponible' : 'Choisir ce service'}
        </button>
      </div>
    </div>
  );
};

// Payment Page Component
const PaymentPage = ({ service, onBack, onSuccess }) => {
  const [processing, setProcessing] = useState(false);
  // Default to cash for web, card for mobile apps
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [paymentData, setPaymentData] = useState({
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    cardName: ''
  });

  // Check if running on web (hide card payment on web)
  const isWeb = true;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPaymentData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    setProcessing(true);

    try {
      // Simulate payment processing
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Create client service via API
      const serviceData = {
        service_type: service.id,
        payment_method: paymentMethod
      };

      await bookingAPI.createClientService(serviceData);

      if (paymentMethod === 'cash') {
        toast.success('Commande confirmée ! Payez en espèces au studio.');
      } else {
        toast.success('Paiement réussi ! Service activé.');
      }
      onSuccess();
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Erreur lors du paiement. Veuillez réessayer.');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <button
          onClick={onBack}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Retour aux services
        </button>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {/* Service Summary */}
          <div className="bg-gray-50 p-6 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Récapitulatif de commande</h2>
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium text-gray-900">{service.name}</h3>
                <p className="text-sm text-gray-600">{service.session_count} séances</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-primary-600">{service.price} MAD</p>
              </div>
            </div>
          </div>

          {/* Payment Form */}
          <form onSubmit={handlePayment} className="p-6">
            {/* Payment Method Selection */}
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Méthode de paiement</h3>

              

              <div className={`grid gap-3 ${isWeb ? 'grid-cols-1' : 'grid-cols-2'}`}>
                {/* Hide card payment on web */}
                {!isWeb && (
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('card')}
                    className={`p-3 border rounded-lg text-center transition-colors ${
                      paymentMethod === 'card'
                        ? 'border-primary-500 bg-primary-50 text-primary-700'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <CreditCardIcon className="h-6 w-6 mx-auto mb-1" />
                    <span className="text-sm font-medium">Carte bancaire</span>
                  </button>
                )}

                <button
                  type="button"
                  onClick={() => setPaymentMethod('cash')}
                  className={`p-3 border rounded-lg text-center transition-colors ${
                    paymentMethod === 'cash'
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <CurrencyDollarIcon className="h-6 w-6 mx-auto mb-1" />
                  <span className="text-sm font-medium">Espèces</span>
                </button>
              </div>
            </div>

            {paymentMethod === 'card' && (
              <>
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                  <CreditCardIcon className="h-5 w-5 mr-2" />
                  Informations de paiement
                </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom sur la carte
                </label>
                <input
                  type="text"
                  name="cardName"
                  value={paymentData.cardName}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Numéro de carte
                </label>
                <input
                  type="text"
                  name="cardNumber"
                  value={paymentData.cardNumber}
                  onChange={handleInputChange}
                  required
                  maxLength="19"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                  placeholder="1234 5678 9012 3456"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date d'expiration
                  </label>
                  <input
                    type="text"
                    name="expiryDate"
                    value={paymentData.expiryDate}
                    onChange={handleInputChange}
                    required
                    maxLength="5"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                    placeholder="MM/YY"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    CVV
                  </label>
                  <input
                    type="text"
                    name="cvv"
                    value={paymentData.cvv}
                    onChange={handleInputChange}
                    required
                    maxLength="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                    placeholder="123"
                  />
                </div>
              </div>
            </div>
            </>
            )}

            {paymentMethod === 'cash' && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <div className="flex items-start">
                  <CurrencyDollarIcon className="h-6 w-6 text-yellow-600 mr-3 mt-0.5" />
                  <div>
                    <h3 className="text-lg font-medium text-yellow-800 mb-2">
                      Paiement en espèces
                    </h3>
                    <p className="text-sm text-yellow-700 mb-3">
                      Votre commande sera confirmée et vous pourrez effectuer le paiement en espèces directement au studio lors de votre première séance.
                    </p>
                    <div className="bg-yellow-100 rounded-lg p-3">
                      <p className="text-sm text-yellow-800 font-medium">
                        📍 Adresse de paiement : Nazaï Studio
                      </p>
                      <p className="text-sm text-yellow-700 mt-1">
                        Veuillez vous présenter 15 minutes avant votre première séance pour effectuer le règlement.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={processing}
              className="w-full mt-6 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-300 text-white py-3 px-4 rounded-lg font-medium transition-colors"
            >
              {processing ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Traitement en cours...
                </div>
              ) : (
                paymentMethod === 'cash'
                  ? `Commander ${service.price} MAD (Paiement en espèces)`
                  : `Payer ${service.price} MAD`
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BookService;
